import { ActionIcon, Button, Loader } from '@mantine/core';
import { sizes, variants } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeneratedIfcModelSummary } from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

interface GeneratedModelListProps {
    models: GeneratedIfcModelSummary[];
    isPending: boolean;
    isBusy: boolean;
    onOpen: (ifcModelId: string) => void;
    onEdit: (ifcModelId: string) => void;
    onDownload: (model: GeneratedIfcModelSummary) => void;
    onDelete: (ifcModelId: string) => Promise<boolean>;
}

const iconClassName = 'action-icon-small bg-transparent hover:bg-background-gray! icon-sm';

/** Stored models with open, edit, download and confirmed delete actions. */
export const GeneratedModelList = ({
    models,
    isPending,
    isBusy,
    onOpen,
    onEdit,
    onDownload,
    onDelete
}: GeneratedModelListProps) => {
    const { t } = useTranslation();
    const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    if (isPending) {
        return <Loader size="xs" />;
    }
    if (models.length === 0) {
        return <div className="py-4 text-center text-xs text-text-light">{t('infoNoGeneratedIfcModels')}</div>;
    }

    const handleDelete = async (ifcModelId: string) => {
        setDeletingId(ifcModelId);
        try {
            if (await onDelete(ifcModelId)) {
                setDeleteCandidateId(null);
            }
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <ul className="space-y-1" data-testid="generated-ifc-models">
            {models.map((model) => (
                <li
                    key={model.id}
                    className="flex items-center justify-between gap-2 rounded-sm bg-background-gray-ultra-light px-2 py-1"
                >
                    <span className="min-w-0">
                        <span className="block truncate text-sm">{model.name}</span>
                        <span
                            className="block truncate text-xs text-text-light"
                            title={model.sourceFileNames.join(', ')}
                        >
                            {new Date(model.generatedAt).toLocaleString()} ·{' '}
                            {t('infoGeneratedIfcModelCounts', { storeys: model.storeyCount, walls: model.wallCount })}
                        </span>
                    </span>
                    {deleteCandidateId === model.id ? (
                        <span className="flex shrink-0 items-center gap-1 text-xs">
                            {t('infoDeleteGeneratedIfcConfirm')}
                            <Button
                                color="red"
                                loading={deletingId === model.id}
                                size={sizes.button.small}
                                onClick={() => void handleDelete(model.id)}
                            >
                                {t('delete')}
                            </Button>
                            <Button
                                disabled={deletingId === model.id}
                                size={sizes.button.small}
                                variant={variants.button.secondary}
                                onClick={() => setDeleteCandidateId(null)}
                            >
                                {t('cancel')}
                            </Button>
                        </span>
                    ) : (
                        <span className="flex shrink-0 gap-1">
                            <ActionIcon
                                aria-label={t('buttonStartWallForming')}
                                className={iconClassName}
                                disabled={isBusy}
                                onClick={() => onOpen(model.id)}
                            >
                                view_in_ar
                            </ActionIcon>
                            <ActionIcon
                                aria-label={t('buttonEditWalls')}
                                className={iconClassName}
                                onClick={() => onEdit(model.id)}
                            >
                                architecture
                            </ActionIcon>
                            <ActionIcon
                                aria-label={t('buttonDownloadIfc')}
                                className={iconClassName}
                                onClick={() => onDownload(model)}
                            >
                                download
                            </ActionIcon>
                            <ActionIcon
                                aria-label={t('buttonDeleteGeneratedIfc')}
                                className={iconClassName}
                                onClick={() => setDeleteCandidateId(model.id)}
                            >
                                delete
                            </ActionIcon>
                        </span>
                    )}
                </li>
            ))}
        </ul>
    );
};
